import { gql, TypedDocumentNode } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { useFormContext } from '@repo/shared-client/form'
import { cn, mergeRefs } from '@repo/shared-client/lib/utils'
import { MIN_PROFILE_NAME_LENGTH } from '@web/src/lib/graphql/profiles/constants'
import type {
  FindprofileNameAvailableQuery,
  FindprofileNameAvailableQueryVariables,
} from '@web/src/lib/graphql/types.docs.generated'
import { useTranslation } from '@web/src/lib/intl/react'
import { CheckIcon, Loader2Icon, XIcon } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { ChangeEvent, forwardRef, useEffect, useId, useState } from 'react'

export const PROFILE_NAME_FIELD_VALID: TypedDocumentNode<
  FindprofileNameAvailableQuery,
  FindprofileNameAvailableQueryVariables
> = gql`
  query FindprofileNameAvailable($name: String!) {
    profileNameAvailable: profileNameAvailable(name: $name) {
      error
      valid
    }
  }
`

interface ProfileNameProps {
  valid: boolean
  error?: string
}

export type SFProfileNameFieldProps = Omit<ComponentProps<typeof Field>, 'children'> & {
  name: string
  defaultValue?: string
  inputProps?: ComponentProps<typeof InputGroupInput>
  label?: ReactNode
  helperText?: ReactNode
  optionalText?: ReactNode
  required?: boolean
  disabled?: boolean
  readOnly?: boolean
}

const SFProfileNameField = forwardRef<HTMLInputElement, SFProfileNameFieldProps>((props, forwardedRef) => {
  const { t } = useTranslation()
  const {
    clearErrors,
    getFieldState,
    getValues,
    register: registerField,
    setError,
    formState: { errors },
  } = useFormContext()

  const inputId = useId()

  const {
    name,
    defaultValue,
    inputProps,
    label,
    helperText,
    optionalText,
    required,
    disabled,
    readOnly,
    className,
    ...fieldRest
  } = props

  const [profileNameAvailableState, setProfileNameAvailableState] = useState<ProfileNameProps>({
    valid: Boolean(defaultValue && defaultValue.length >= MIN_PROFILE_NAME_LENGTH),
    error: null,
  })

  const [checkprofileNameAvailable, profileCheckStatus] = useLazyQuery(PROFILE_NAME_FIELD_VALID, {
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    const profileNameData = profileCheckStatus.data?.profileNameAvailable
    if (!profileNameData) {
      return
    }

    const queriedName = profileCheckStatus.variables?.name
    const currentName = getValues(name)
    if (typeof queriedName === 'string' && queriedName !== currentName) {
      return
    }

    setProfileNameAvailableState((current) =>
      current.valid === profileNameData.valid && current.error === profileNameData.error ? current : profileNameData
    )

    const currentMessage = getFieldState(name).error?.message
    if (profileNameData.error) {
      if (currentMessage !== profileNameData.error) {
        setError(name, { type: 'error', message: profileNameData.error })
      }
    } else if (currentMessage) {
      clearErrors(name)
    }
    // `useFormContext()` returns a new object whenever form state changes. Depend on the
    // stable method identities so applying the availability result does not re-run this effect.
  }, [clearErrors, getFieldState, getValues, name, profileCheckStatus.data, profileCheckStatus.variables, setError])

  const { ref: registerRef, ...register } = registerField(name, {
    validate: {
      checkNameValid: () =>
        profileNameAvailableState?.valid
          ? true
          : profileNameAvailableState?.error || t('Profile name is already in use'),
      checkNameValidNotNull: (value) =>
        (profileNameAvailableState !== null && profileNameAvailableState !== undefined) ||
        value === defaultValue ||
        value.length >= MIN_PROFILE_NAME_LENGTH ||
        t('Server profile name validation in progress.'),
    },
    required: t('Profile name is required'),
  })

  const {
    ref: inputRefFromProps,
    className: inputClassName,
    onChange: inputOnChange,
    ...restInputProps
  } = inputProps ?? {}

  const handleProfileNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    register.onChange(event)
    inputOnChange?.(event)
    const profileName = event.target.value

    clearErrors(name)

    if (profileName === defaultValue) {
      setProfileNameAvailableState({ valid: true })
    } else {
      setProfileNameAvailableState({ valid: false })
      checkprofileNameAvailable({ variables: { name: profileName } })
    }
  }

  const error = errors[name]
  const invalid = !!error

  return (
    <Field className={cn(className)} data-invalid={invalid} {...fieldRest}>
      {label !== undefined ? (
        <FieldLabel className="flex items-center gap-1" htmlFor={inputId}>
          {label}
          {required ? (
            <span aria-hidden className="text-destructive">
              *
            </span>
          ) : optionalText ? (
            <span className="font-normal text-muted-foreground text-sm">{optionalText}</span>
          ) : null}
        </FieldLabel>
      ) : null}
      <InputGroup className="w-full">
        <InputGroupAddon
          align="inline-start"
          className="min-w-11 max-w-11 justify-center border-0 bg-transparent px-1 py-0"
        >
          {profileCheckStatus.loading ? (
            <Loader2Icon aria-hidden className="size-5 animate-spin text-muted-foreground" />
          ) : null}
          {!profileCheckStatus.loading && profileNameAvailableState.valid ? (
            <CheckIcon aria-hidden className="size-5 text-green-600 dark:text-green-500" />
          ) : null}
          {profileCheckStatus.loading || profileNameAvailableState.valid ? null : (
            <Tooltip>
              <TooltipTrigger render={<span className="inline-flex cursor-default items-center justify-center" />}>
                <XIcon aria-hidden className="size-5 text-destructive" />
              </TooltipTrigger>
              <TooltipContent>{t('Profile name is already in use')}</TooltipContent>
            </Tooltip>
          )}
        </InputGroupAddon>
        <InputGroupInput
          suppressHydrationWarning
          {...register}
          {...restInputProps}
          aria-invalid={invalid}
          className={cn(inputClassName)}
          disabled={disabled ?? restInputProps.disabled}
          id={inputId}
          onChange={handleProfileNameChange}
          readOnly={readOnly ?? restInputProps.readOnly}
          ref={mergeRefs(registerRef, forwardedRef, inputRefFromProps)}
          required={required}
          type="text"
        />
      </InputGroup>
      {helperText !== undefined ? <FieldDescription>{helperText}</FieldDescription> : null}
      {error?.message ? <FieldError>{String(error.message)}</FieldError> : null}
    </Field>
  )
})

SFProfileNameField.displayName = 'SFProfileNameField'

export default SFProfileNameField
